/**
 * Продающий список преимуществ подписки inFLOW.
 *
 * Единый источник копирайта — используется и в разделе «Подписка»
 * (Subscription.tsx selling-hero) и на отдельной продающей странице
 * промокода (Promo.tsx), чтобы не дублировать текст.
 */

import type { KBCountsOut } from "@/api/types";

/** Скільки контенту в Базі знань — з `/knowledge-base/counts`. */
export interface KbCounts {
  articles: number;
  podcasts: number;
  lessons: number;
}

export interface Benefit {
  icon: string;
  title: string;
  /** Функція — коли в описі живі числа (База знань, owner 2026-09-27). */
  desc: string | ((kb: KbCounts | null) => string);
}

function plural(n: number, one: string, few: string, many: string): string {
  const m10 = n % 10;
  const m100 = n % 100;
  if (m10 === 1 && m100 !== 11) return one;
  if (m10 >= 2 && m10 <= 4 && (m100 < 12 || m100 > 14)) return few;
  return many;
}

/** Числа з відповіді `/knowledge-base/counts`. Немає хоч одного — null. */
export function kbCountsFrom(data: KBCountsOut | undefined): KbCounts | null {
  return data && data.articles > 0 && data.podcasts > 0 && data.lessons > 0 ? data : null;
}

export function benefitDesc(b: Benefit, kb: KbCounts | null): string {
  return typeof b.desc === "function" ? b.desc(kb) : b.desc;
}

export const SUBSCRIPTION_BENEFITS: Benefit[] = [
  {
    icon: "⚡",
    title: "Миттєві вакансії",
    desc: "Усі вакансії приходять за секунду після публікації (Threads, Telegram-чати й канали, фріланс-біржі). Без підписки\u00a0— затримка 4 години, а за цей час половина вже неактуальна.",
  },
  {
    icon: "🔓",
    title: "Контакти без обмежень",
    desc: "Вакансії приходять одразу з відкритими контактами замовників. На FREE\u00a0— лише 5 контактів на тиждень.",
  },
  {
    icon: "🎯",
    title: "Ексклюзивні вакансії",
    desc: "inFLOW Finder\u00a0— наш бот, де замовники, клієнти й підприємці самі розміщують вакансії. Ці вакансії ексклюзивні й доступні лише з підпискою.",
  },
  {
    icon: "🤖",
    title: "AI-генерація відповідей",
    desc: "AI аналізує твій профіль і текст вакансії та пише індивідуальну відповідь на вакансію. Скопіював\u00a0— відправив\u00a0— закрив клієнта.",
  },
  {
    icon: "📝",
    title: "Генерація відповідей",
    desc: "Користуйся трьома нашими промптами й додай до пʼяти своїх\u00a0— AI писатиме відповіді на вакансії у твоєму стилі.",
  },
  {
    icon: "👀",
    title: "Фільтри вакансій",
    desc: "Мова, формат роботи, «Вакансії чи замовлення» й чорний список слів\u00a0— приходить лише те, що тобі підходить.",
  },
  {
    icon: "📨",
    title: "Оформлення вакансій",
    desc: "Повний текст вакансії або короткий AI-переказ\u00a0— обираєш сам.",
  },
  {
    icon: "📚",
    title: "База знань",
    desc: (kb) =>
      kb
        ? `${kb.podcasts} ${plural(kb.podcasts, "подкаст", "подкасти", "подкастів")} і ${kb.articles} ${plural(kb.articles, "стаття", "статті", "статей")} від засновника inFLOW\u00a0+ ${kb.lessons} ${plural(kb.lessons, "урок", "уроки", "уроків")} від десятка спікерів: продажі, мислення, гроші, масштабування, AI.`
        : "Подкасти й статті від засновника inFLOW\u00a0+ уроки від десятка спікерів: продажі, мислення, гроші, масштабування, AI.",
  },
  {
    icon: "🔖",
    title: "Збережені вакансії",
    desc: "Відкладай вакансію одним тапом\u00a0— у боті або в застосунку\u00a0— і повертайся до неї, коли буде час написати замовнику.",
  },
  {
    icon: "🎬",
    title: "Курс від засновника inFLOW",
    desc: "6 уроків\u00a0+ записаний ефір: легкі продажі, ціна та пошук клієнтів.",
  },
  {
    icon: "⏳",
    title: "Заморозка підписки",
    desc: "3 дні заморозки за кожен оплачений місяць\u00a0— на відпустку чи паузу.",
  },
];
